/**
 * Shared pillar data used by /services (overview hub) and
 * /services/<slug> (deep-dive pages). Keep this file as the single
 * source of truth for the 4 SEO pillars so we never duplicate copy.
 */
import { Code2, Search, Bot, Landmark } from "lucide-react";

export const PILLARS = [
  {
    id: "web-design",
    slug: "web-design",
    kicker: "01",
    Icon: Code2,
    name: "Web Design & Development",
    hubTagline: "Fast, accessible, conversion-focused websites — hand-coded, never templated.",
    metaTitle: "Custom Web Design & Development — Redkey Web Design",
    metaDescription:
      "Custom web design and development for businesses across the United States. Hand-coded, responsive, accessible websites engineered for performance, SEO, and long-term growth.",
    heroLine:
      "Custom websites that look right, load fast, and convert — hand-coded by a senior developer.",
    intro:
      "Redkey Web Design builds custom business websites, marketing sites, e-commerce platforms, and web applications engineered around a real content strategy — not a page-builder preset. Every site ships with a locked-down component system, responsive image pipelines, and semantic HTML that pays dividends in Core Web Vitals and organic search visibility for years.",
    approachLead: "Our approach to custom web design & development",
    approach: [
      {
        heading: "Strategy first, pixels second",
        body: "We start with your customers, your business goals, and the queries they actually type into Google. The site's information architecture flows from that — not from a template's opinion.",
      },
      {
        heading: "Design in the browser",
        body: "We prototype in real code from day one. That means what you approve is what ships — no handoff surprises, no 'we can't build that after all' conversations.",
      },
      {
        heading: "Performance & accessibility as a floor",
        body: "Every site targets sub-second LCP, passes Core Web Vitals, and meets WCAG 2.2 AA. These aren't upgrades. They're the baseline.",
      },
      {
        heading: "Semantic, search-friendly HTML",
        body: "The markup is engineered for Google, screen readers, and future you. Structured data, canonical hygiene, and a clean internal-linking model come standard.",
      },
    ],
    included: [
      "Custom web design & development",
      "Website redesign & re-platform",
      "Responsive, mobile-first builds",
      "E-commerce (Shopify, Stripe, headless)",
      "Web applications & internal tools",
      "Ongoing website maintenance",
    ],
    keywords: [
      "Custom Web Design", "Website Development", "Website Redesign",
      "Responsive Web Design", "CMS Development", "Business Websites",
      "Website Maintenance", "Website Performance",
    ],
    relatedWorkIds: ["riverside-tv", "amber-rdh", "golden-candids"],
    faq: [
      {
        q: "How much does a custom website cost?",
        a: "Marketing sites typically run $8k–$25k depending on scope, content, and integrations. E-commerce and Drupal builds are quoted separately. Every project starts with a fixed written scope and price — no hourly surprises.",
      },
      {
        q: "How long does a custom website take?",
        a: "Most marketing sites ship in 4–8 weeks. E-commerce and larger CMS builds run 8–14 weeks. Timeline depends more on content readiness than on development speed.",
      },
      {
        q: "Do you offer website redesign for existing businesses?",
        a: "Yes. Redesign engagements typically include a content audit, information-architecture rebuild, and a full-quality technical migration with redirects preserved — so you keep your SEO equity instead of resetting it.",
      },
      {
        q: "Is website maintenance available after launch?",
        a: "Yes. Monthly retainers cover security updates, accessibility drift, content refreshes, and continuous performance monitoring. Long-term partnerships are the model here — not launch-and-leave.",
      },
      {
        q: "Do you build responsive, mobile-first websites?",
        a: "Every Redkey Web Design site is mobile-first, tested on real devices, and engineered to render beautifully on desktops, tablets, and phones.",
      },
    ],
  },

  {
    id: "seo",
    slug: "seo",
    kicker: "02",
    Icon: Search,
    name: "SEO & Digital Growth",
    hubTagline: "Technical SEO, content architecture, and lead-generation systems that compound.",
    metaTitle: "SEO Services — Technical SEO, Local SEO & Content Strategy — Redkey Web Design",
    metaDescription:
      "SEO services that build lasting search visibility: technical SEO, local SEO, content strategy, structured data, and Core Web Vitals optimization for U.S. businesses and organizations.",
    heroLine:
      "Search visibility that compounds — technical SEO, content architecture, and lead-generation systems.",
    intro:
      "Search visibility isn't a plug-in. We build sites with the technical bones — schema, semantic markup, sitemap and canonical hygiene, Core Web Vitals, internal linking — that let search engines actually understand you. Combined with a clear content strategy focused on the queries your customers already type, that produces qualified organic traffic and measurable lead flow.",
    approachLead: "Our approach to SEO & digital growth",
    approach: [
      {
        heading: "Technical SEO audit first",
        body: "Before we touch content, we audit crawl paths, indexation, Core Web Vitals, structured data, and canonical hygiene. Then we remediate the issues search engines actually penalize.",
      },
      {
        heading: "Content that answers real queries",
        body: "We map topical clusters against the questions your customers type — not the ones an AI tool suggests in a vacuum. Each piece is written to earn a place on page one.",
      },
      {
        heading: "Structured data for rich results",
        body: "FAQPage, Organization, LocalBusiness, Article, Product — we wire up the schema types that unlock rich snippets in Google's SERPs and take up more real estate.",
      },
      {
        heading: "Measurable growth, not vanity metrics",
        body: "We track qualified organic leads, not rankings for keywords nobody searches for. Every engagement reports against traffic that turns into revenue.",
      },
    ],
    included: [
      "Technical SEO audits & remediation",
      "Site architecture & internal linking",
      "Content strategy & topical clusters",
      "Structured data & rich results",
      "Core Web Vitals optimization",
      "Local SEO & Google Business Profile",
    ],
    keywords: [
      "SEO Services", "Technical SEO", "Local SEO",
      "Content Strategy", "Website Optimization", "Organic Traffic",
      "Lead Generation", "Search Visibility",
    ],
    relatedWorkIds: ["aaa-tree", "amber-rdh", "join-rfd"],
    faq: [
      {
        q: "What is technical SEO?",
        a: "Technical SEO is the set of on-site and infrastructure improvements — crawlability, indexation, Core Web Vitals, structured data, canonical tags, sitemap and robots hygiene, internal linking, page speed — that lets search engines understand and rank your content. It's the foundation before content strategy.",
      },
      {
        q: "Do you offer local SEO for small businesses?",
        a: "Yes. Local SEO engagements include Google Business Profile optimization, local schema markup, citation cleanup, and service-area content — the same playbook we ran for AAA Tree Service and Amber RDH AP.",
      },
      {
        q: "Can Redkey Web Design improve my search visibility without a redesign?",
        a: "Often, yes. Many sites are one technical audit away from meaningfully better organic traffic. Redesigns happen only when the existing site's foundation is holding you back.",
      },
      {
        q: "How long until we see SEO results?",
        a: "Most engagements see meaningful movement in 90 days: crawl coverage, indexation, and rankings for lower-competition queries. Competitive terms take 6–12 months of sustained work.",
      },
      {
        q: "Do you use black-hat tactics or link-buying?",
        a: "No. We build for the long game — earned links, quality content, and technical excellence. Nothing that could get your site penalized.",
      },
    ],
  },

  {
    id: "ai-automation",
    slug: "ai-automation",
    kicker: "03",
    Icon: Bot,
    name: "AI Automation",
    hubTagline: "AI-powered lead qualification, content workflows, and business process automation.",
    metaTitle: "AI Automation Services — Workflow, Lead Qualification & Content — Redkey Web Design",
    metaDescription:
      "AI automation and workflow services that actually earn their keep: lead qualification, content workflows, customer support triage, and process automation for U.S. businesses.",
    heroLine:
      "AI where it earns its keep — lead qualification, content workflows, and process automation, done right.",
    intro:
      "Not chatbots for the sake of chatbots. We wire AI into the specific places it earns its keep: qualifying inbound leads before they hit a human inbox, drafting first-pass content that a marketer edits instead of writes, automating repetitive customer-support tickets, and cleaning up the operational drag that eats a small team's week.",
    approachLead: "Our approach to AI automation",
    approach: [
      {
        heading: "Start with the workflow, not the model",
        body: "We map the workflow first: what's the repetitive task, what's the human's role, what's the failure mode? AI is the tool at the end, not the starting point.",
      },
      {
        heading: "Small, verifiable wins",
        body: "We ship the smallest automation that produces a measurable result — a lead qualifier, a support triager, a content drafter — and prove ROI before scaling.",
      },
      {
        heading: "Human-in-the-loop by default",
        body: "AI drafts, humans approve. Every automation ships with a clear handoff back to a real person, so quality and brand voice never drift.",
      },
      {
        heading: "Own your data, own your prompts",
        body: "No lock-in to a single vendor. We integrate with OpenAI, Anthropic, Google, and open-source models where each earns its place — and you keep the prompts and the data.",
      },
    ],
    included: [
      "AI lead qualification & routing",
      "Workflow & process automation",
      "AI content drafting pipelines",
      "AI customer support triage",
      "Custom LLM integrations",
      "Automation strategy & audit",
    ],
    keywords: [
      "AI Automation", "Workflow Automation", "AI Business Solutions",
      "AI Customer Support", "AI Lead Qualification", "AI Content Workflows",
      "AI Process Automation",
    ],
    relatedWorkIds: ["riverside-tv", "join-rfd", "aaa-tree"],
    faq: [
      {
        q: "What is AI automation for a business?",
        a: "AI automation means using large-language models and workflow tools to handle repetitive knowledge work: qualifying inbound leads, drafting content, triaging customer-support tickets, and cleaning data. Done right, it gives small teams the leverage of much larger ones — without replacing the humans who make the final call.",
      },
      {
        q: "Will AI automation replace my team?",
        a: "No. Our engagements always keep a human in the loop. AI handles the repetitive first pass; your team makes the judgement calls and owns the final output. The goal is leverage, not layoffs.",
      },
      {
        q: "Which AI providers do you work with?",
        a: "OpenAI, Anthropic (Claude), Google (Gemini), and open-source models like Llama and Mistral. We pick the model that fits the workflow — no vendor lock-in.",
      },
      {
        q: "How much does an AI automation engagement cost?",
        a: "Discovery + a first automation typically runs $5k–$15k. Ongoing tuning and additional workflows are quoted separately. We only recommend AI where the payback is measurable.",
      },
      {
        q: "Is AI-drafted content safe for SEO?",
        a: "Only when a human edits, adds first-party expertise, and takes responsibility for accuracy. Google's guidance is clear: helpful content that demonstrates real experience is fine regardless of how it started. Our pipelines are built around that rule.",
      },
    ],
  },

  {
    id: "drupal-government",
    slug: "drupal-government",
    kicker: "04",
    Icon: Landmark,
    name: "Drupal, WordPress & Government Websites",
    hubTagline: "Accessible, secure Drupal, WordPress, and custom CMS builds for enterprise, government, and public-sector organizations.",
    metaTitle: "Drupal, WordPress & Government Website Development — Redkey Web Design",
    metaDescription:
      "Enterprise-grade Drupal, WordPress, and custom CMS development for government, municipal, and public-sector organizations. WCAG 2.2 accessible, Section 508 compliant, secure by default.",
    heroLine:
      "Enterprise-grade Drupal, WordPress, and custom CMS development — for government, municipal, and enterprise organizations.",
    intro:
      "Municipal, state, and public-sector work is different. It has to be accessible to every resident, resilient under peak traffic, secure by default, and maintainable by non-technical editors across dozens of departments. We specialize in Drupal, WordPress, and custom CMS builds and migrations for government agencies, universities, and enterprise organizations where those constraints are the requirement, not the wishlist.",
    approachLead: "Our approach to Drupal, WordPress & government websites",
    approach: [
      {
        heading: "Accessibility as a floor, not a ceiling",
        body: "Every build targets WCAG 2.2 AA at minimum, with government and healthcare pushed to AAA where feasible. Section 508 compliance is baked into the design system.",
      },
      {
        heading: "Governance that survives staff turnover",
        body: "Multi-department CMS deployments only work if the governance model is right: locked component libraries, editorial workflows, and clear ownership so departments can move fast without breaking the system.",
      },
      {
        heading: "Migration without SEO loss",
        body: "Drupal-to-Drupal upgrades, WordPress-to-Drupal migrations, custom-CMS rebuilds — we plan the redirect map before we touch a line of code. You keep your organic equity.",
      },
      {
        heading: "Security & resilience by default",
        body: "Government-grade security posture: locked module lists, security-only update windows, HTTPS/HSTS, CSP, edge caching, and load-tested for the seasonal traffic peaks that actually matter.",
      },
    ],
    included: [
      "Drupal 10 development & migration",
      "WordPress development & migration",
      "Custom CMS development",
      "Government & municipal websites",
      "Multi-department design systems",
      "WCAG 2.2 accessibility (AA / AAA)",
      "Section 508 compliance",
      "Editor training & governance",
    ],
    keywords: [
      "Drupal Development", "Drupal Migration", "WordPress Development",
      "WordPress Migration", "Custom CMS Development", "Government Website Development",
      "Municipal Websites", "Public Sector Websites", "Accessible Government Websites",
      "WCAG Compliance", "ADA Accessibility", "Section 508",
    ],
    relatedWorkIds: ["city-of-riverside", "riverside-tv", "festival-of-lights", "join-rfd"],
    faq: [
      {
        q: "Do you build websites for government agencies and municipalities?",
        a: "Yes. Our public-sector work includes the City of Riverside (serving 320,000+ residents across 40+ departments), the ADDY Award-winning RiversideTV municipal media platform, the Festival of Lights (500,000+ annual visitors), and the Riverside Fire Department recruitment site. Every one accessible, secure, and maintained.",
      },
      {
        q: "Do you handle Drupal migrations?",
        a: "Yes. Drupal 7-to-10 upgrades, WordPress-to-Drupal migrations, and custom CMS rebuilds. Every migration starts with a redirect map so you don't lose SEO equity, and a content audit so you're not migrating pages nobody reads.",
      },
      {
        q: "Are your Drupal and WordPress sites WCAG-compliant?",
        a: "Yes. Every build targets WCAG 2.2 AA at minimum. Government and healthcare projects push to AAA where feasible. Compliance is designed in, not audited after — because auditing an inaccessible site is a redesign in disguise.",
      },
      {
        q: "What is Section 508 compliance?",
        a: "Section 508 is a U.S. federal accessibility law requiring that government electronic content be accessible to people with disabilities. In practice it maps closely to WCAG 2.0/2.1 AA. Every Redkey Web Design government project meets or exceeds it.",
      },
      {
        q: "Do you offer long-term Drupal or WordPress maintenance?",
        a: "Yes. Monthly retainers cover security updates, core and module upgrades, accessibility drift monitoring, performance tuning, and continuous editor support. Public-sector work especially benefits from a stable long-term partnership.",
      },
      {
        q: "Can editors update the site without breaking it?",
        a: "Yes — that's the point of the way we build. Component libraries are locked, page templates are opinionated, and non-technical staff can publish confidently without the site drifting visually or losing accessibility compliance.",
      },
    ],
  },
];

export const getPillar = (slug) => PILLARS.find((p) => p.slug === slug);
